import moment from 'moment';
import { getLocalTime, getUTCTimeFromLocal } from '../time/time';

const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const DEFAULT_TIMEZONE = 'Australia/Melbourne';

export const normalizeCalendarDateKey = (value) => {
	if (!value) {
		return null;
	}

	if (typeof value === 'string') {
		const trimmed = value.trim();
		const parsedDateKey = moment(trimmed, DATE_KEY_FORMAT, true);
		if (parsedDateKey.isValid()) {
			return parsedDateKey.format(DATE_KEY_FORMAT);
		}
	}

	const parsed = moment(value);
	return parsed.isValid() ? parsed.format(DATE_KEY_FORMAT) : null;
};

export const normalizeDateKey = (value, timezone) => {
	if (!value) {
		return null;
	}

	if (typeof value === 'number' && Number.isFinite(value)) {
		const parsedNumericDate = new Date(value);
		if (!Number.isNaN(parsedNumericDate.getTime())) {
			return timezone
				? getLocalTime(timezone, parsedNumericDate).format(DATE_KEY_FORMAT)
				: parsedNumericDate.toISOString().slice(0, 10);
		}
	}

	if (typeof value === 'string') {
		const trimmed = value.trim();
		const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
		if (match && (trimmed === match[1] || !timezone)) {
			return match[1];
		}

		if (/^\d+$/.test(trimmed)) {
			const numericValue = Number(trimmed);
			if (Number.isFinite(numericValue)) {
				const parsedNumericDate = new Date(numericValue);
				if (!Number.isNaN(parsedNumericDate.getTime())) {
					return timezone
						? getLocalTime(timezone, parsedNumericDate).format(DATE_KEY_FORMAT)
						: parsedNumericDate.toISOString().slice(0, 10);
				}
			}
		}

		const parsedStringDate = new Date(trimmed);
		if (!Number.isNaN(parsedStringDate.getTime())) {
			return timezone
				? getLocalTime(timezone, parsedStringDate).format(DATE_KEY_FORMAT)
				: parsedStringDate.toISOString().slice(0, 10);
		}
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}

	return timezone
		? getLocalTime(timezone, parsed).format(DATE_KEY_FORMAT)
		: parsed.toISOString().slice(0, 10);
};

export const serializeDateKeyForTimezone = (value, timezone) => {
	const dateKey = normalizeCalendarDateKey(value);
	if (!dateKey) {
		return null;
	}

	const ownerLocalNoon = moment(dateKey, DATE_KEY_FORMAT, true)
		.hours(12)
		.minutes(0)
		.seconds(0)
		.milliseconds(0);

	return getUTCTimeFromLocal(timezone || DEFAULT_TIMEZONE, ownerLocalNoon).toISOString();
};

export const formatExpiryDateForDisplay = (value) => {
	const dateKey = normalizeDateKey(value);
	if (!dateKey) {
		return null;
	}

	const parsed = moment(dateKey, 'YYYY-MM-DD', true);
	if (!parsed.isValid()) {
		return dateKey;
	}

	return parsed.format('D MMM YYYY');
};

export const buildExpiryBatchSummaryFromBatches = (batches = [], { timezone } = {}) => {
	const summary = {
		rows: [],
		unknownQuantity: 0,
		totalQuantity: 0,
	};

	const quantitiesByDate = {};

	batches.forEach(batch => {
		const quantity = Math.max(0, Number(batch?.quantityOnHand ?? batch?.quantity ?? 0) || 0);
		if (!quantity) {
			return;
		}

		summary.totalQuantity += quantity;
		const dateKey = normalizeDateKey(batch?.expiryDate, timezone);
		if (!dateKey) {
			summary.unknownQuantity += quantity;
			return;
		}

		quantitiesByDate[dateKey] = (quantitiesByDate[dateKey] || 0) + quantity;
	});

	summary.rows = Object.keys(quantitiesByDate)
		.sort()
		.map(expiryDate => ({
			expiryDate,
			quantity: quantitiesByDate[expiryDate],
		}));

	return summary;
};

export const formatExpiryBatchSummaryText = (summary, {
	maxRows = 3,
	fallback = 'n/a',
} = {}) => {
	const rows = summary?.rows || [];
	const unknownQuantity = Number(summary?.unknownQuantity || 0);

	if (!rows.length && !unknownQuantity) {
		return fallback;
	}

	const dateParts = rows
		.slice(0, maxRows)
		.map(row => {
			const expiryDateLabel = formatExpiryDateForDisplay(row.expiryDate);
			if (!expiryDateLabel) {
				return null;
			}

			return `${expiryDateLabel}: ${Number(row.quantity || 0)}`;
		})
		.filter(Boolean);

	const sections = [...dateParts];
	if (rows.length > maxRows) {
		sections.push(`+${rows.length - maxRows} more`);
	}
	if (unknownQuantity) {
		sections.push(`Unknown: ${unknownQuantity}`);
	}

	return sections.length ? sections.join(' | ') : fallback;
};

export const formatExpiryBatchesText = (batches = [], options) => {
	return formatExpiryBatchSummaryText(buildExpiryBatchSummaryFromBatches(batches, options), options);
};
